/** The shell: tabs, sheets, and the one always-present action. */
import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Icons, Scale10, Sheet, Toast } from './ui/bits';
import { Today } from './views/Today';
import { Food } from './views/Food';
import { Train, SessionSheet } from './views/Train';
import { Peptides } from './views/Peptides';
import { Settings } from './views/Settings';
import { Trends } from './views/Trends';
import { FoodSheet } from './views/FoodSheet';
import { DoseSheet } from './views/DoseSheet';
import { checkinToday, init, ready, saveCheckIn, toast, today } from './store';
import { GI_TAGS, type GiTag } from './data/schema';
import { toDay } from './lib/time';
import type { Routine } from './data/schema';

type Tab = 'today' | 'food' | 'train' | 'peptides' | 'more';

const TABS: { id: Tab; label: string; icon: JSX.Element }[] = [
  { id: 'today', label: 'Today', icon: Icons.today },
  { id: 'food', label: 'Food', icon: Icons.food },
  { id: 'train', label: 'Train', icon: Icons.train },
  { id: 'peptides', label: 'Pin', icon: Icons.pin },
  { id: 'more', label: 'More', icon: Icons.more },
];

type SheetState =
  | { kind: 'food' }
  | { kind: 'dose' }
  | { kind: 'checkin' }
  | { kind: 'session'; routine?: Routine | undefined }
  | null;

export function App(): JSX.Element {
  const [tab, setTab] = useState<Tab>('today');
  const [sheet, setSheet] = useState<SheetState>(null);
  const [trends, setTrends] = useState(false);

  useEffect(() => { void init(); }, []);

  // The app stays open for days on a phone. Roll the day over when it changes
  // rather than showing yesterday's log until a manual reload.
  useEffect(() => {
    const check = () => {
      const now = toDay();
      if (now !== today.value) { today.value = now; void init(); }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, []);

  const close = () => setSheet(null);

  return (
    <div class="app">
      <header class="topbar">
        {trends ? (
          <button class="btn btn-sm btn-ghost" style={{ marginLeft: '-8px' }} onClick={() => setTrends(false)}>‹ Back</button>
        ) : null}
        <h1>{trends ? 'Trends' : TABS.find((t) => t.id === tab)?.label ?? 'Reta'}</h1>
        {trends ? <span /> : null}
      </header>

      <main class="screen">
        {!ready.value ? null : trends ? (
          <Trends />
        ) : tab === 'today' ? (
          <Today
            onLogFood={() => setSheet({ kind: 'food' })}
            onLogDose={() => setSheet({ kind: 'dose' })}
            onCheckIn={() => setSheet({ kind: 'checkin' })}
            onTrends={() => setTrends(true)}
          />
        ) : tab === 'food' ? (
          <Food onLog={() => setSheet({ kind: 'food' })} />
        ) : tab === 'train' ? (
          <Train onStart={(routine) => setSheet({ kind: 'session', routine })} />
        ) : tab === 'peptides' ? (
          <Peptides onLogDose={() => setSheet({ kind: 'dose' })} onTrends={() => setTrends(true)} />
        ) : (
          <Settings />
        )}
      </main>

      {tab === 'today' && !trends ? (
        <button class="fab" onClick={() => setSheet({ kind: 'food' })} aria-label="Log food">
          {Icons.plus} Log
        </button>
      ) : null}
      {tab === 'train' && !trends ? (
        <button class="fab" onClick={() => setSheet({ kind: 'session', routine: undefined })} aria-label="Log session">
          {Icons.plus} Session
        </button>
      ) : null}

      <nav class="tabbar" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            class="tab"
            aria-current={tab === t.id ? 'page' : undefined}
            onClick={() => { setTab(t.id); setTrends(false); }}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      {sheet?.kind === 'food' ? <FoodSheet onClose={close} /> : null}
      {sheet?.kind === 'dose' ? <DoseSheet onClose={close} /> : null}
      {sheet?.kind === 'session' ? <SessionSheet routine={sheet.routine} onClose={close} /> : null}
      {sheet?.kind === 'checkin' ? <CheckInSheet onClose={close} /> : null}

      {toast.value ? <Toast text={toast.value.text} error={toast.value.error} /> : null}
    </div>
  );
}

/** The daily subjective read: how the gut, energy, hunger and cravings are. */
function CheckInSheet(props: { onClose: () => void }): JSX.Element {
  const c = checkinToday.value;
  const rows: { key: 'gi' | 'energy' | 'hunger' | 'cravings' | 'sleepQuality' | 'mood'; label: string; hint: string }[] = [
    { key: 'gi', label: 'GI', hint: '0 awful · 10 settled' },
    { key: 'energy', label: 'Energy', hint: '0 flat · 10 sharp' },
    { key: 'hunger', label: 'Hunger', hint: '0 none · 10 constant' },
    { key: 'cravings', label: 'Cravings', hint: '0 none · 10 loud' },
    { key: 'mood', label: 'Mood', hint: '0 low · 10 good' },
    { key: 'sleepQuality', label: 'Sleep felt', hint: '0 poor · 10 great' },
  ];

  return (
    <Sheet
      title="Check-in"
      onClose={props.onClose}
      footer={<button class="btn btn-primary btn-full" onClick={props.onClose}>Done</button>}
    >
      {rows.map((row) => (
        <div key={row.key}>
          <div class="row-between" style={{ marginBottom: '6px' }}>
            <span class="label">{row.label}</span>
            <span class="label">{row.hint}</span>
          </div>
          <Scale10 value={c?.[row.key]} onChange={(v) => void saveCheckIn({ [row.key]: v })} />
          {row.key === 'gi' ? (
            <div class="row wrap" style={{ gap: '6px', marginTop: '8px' }}>
              {GI_TAGS.map((tag) => {
                const on = (c?.giTags ?? []).includes(tag);
                return (
                  <button key={tag} class="chip" aria-pressed={on} style={{ minHeight: '30px' }}
                    onClick={() => {
                      const cur = c?.giTags ?? [];
                      const next: GiTag[] = on ? cur.filter((t) => t !== tag) : [...cur, tag];
                      void saveCheckIn({ giTags: next });
                    }}>{tag}</button>
                );
              })}
            </div>
          ) : null}
        </div>
      ))}
      <p class="note">
        Saved as you tap. These sit alongside your doses and weight so patterns show up over time.
      </p>
    </Sheet>
  );
}
